import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Download, ListChecks, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface VerificationPageHeaderProps {
  chatActive: boolean;
  onToggleChat: () => void;
  showExport: boolean;
  onExport: () => void;
  onBack: () => void;
}

/**
 * The teal is hard-coded rather than tokenised because it always was: the chat
 * affordance is deliberately off-theme and does not follow the light/dark switch.
 *
 * Both states moved one step down the same teal ramp: #00897b gave 4.32:1 at
 * 12px, short of AA. #00796b was already the hover colour here, so the ramp is
 * unchanged — only which end of it each state sits on.
 *
 * Active carries its own background, so one value is legible under either
 * theme. Idle is teal text on the page, which is not: the same teal cannot
 * clear AA on both a white and a near-black surface, so that state alone steps
 * up the ramp in dark. It stays off-palette either way, which is the point.
 */
const CHAT_ACTIVE = 'bg-[#00796b] text-white hover:bg-[#00695c]';
const CHAT_IDLE = 'text-[#00796b] border-[#00796b] dark:text-[#26a69a] dark:border-[#26a69a]';

const VerificationPageHeader: React.FC<VerificationPageHeaderProps> = ({
  chatActive,
  onToggleChat,
  showExport,
  onExport,
  onBack,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-4 p-4 pb-2">
      <Button
        variant="ghost"
        size="icon-sm"
        className="[&_svg]:size-5"
        aria-label={t('common.backToDashboard')}
        onClick={onBack}
      >
        <ArrowLeft strokeWidth={1.9} />
      </Button>
      <ListChecks className="size-7 shrink-0 text-primary" strokeWidth={1.6} aria-hidden="true" />
      <h1 className="revy-cjk text-xl font-medium leading-[1.5]">{t('verification.title')}</h1>

      <div className="flex-1" />

      {/* Filled vs outlined is the on/off state, not emphasis. The provider is
          local because the header renders standalone in tests, but it carries no
          delayDuration so these tooltips match the rest of the shell. */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={chatActive ? 'default' : 'outline'}
              aria-pressed={chatActive}
              size="sm"
              className={cn(chatActive ? CHAT_ACTIVE : CHAT_IDLE)}
              onClick={onToggleChat}
            >
              <MessageSquare strokeWidth={1.9} />
              <span className="revy-cjk">{t('verification.chat.title')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent className="revy-ui revy-cjk">
            {chatActive ? t('verification.chat.closeTooltip') : t('verification.chat.openTooltip')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {showExport && (
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download strokeWidth={1.9} />
          <span className="revy-cjk">{t('verification.export.excel')}</span>
        </Button>
      )}
    </div>
  );
};

export default VerificationPageHeader;
